(ns ui.view.kanban-opensearch.event
  (:require
   [clojure.set :as cset]
   [frontend.actions.entity :as entity-actions]
   [frontend.global-events.toast :as toast]
   [frontend.logging :as logging]
   [common.localization :as loc]
   [common.opensearch.kanban-sort :as kanban-sort]
   [common.opensearch.utils :as os-utils]
   [common.schema.entities.pipeline :as pipeline]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [frontend.view.entity :as f-entity]
   [fx.ajax :as ajax]
   [potpuri.core :as potpuri]
   [re-frame.core :as rf]
   [ui.view.kanban-opensearch.model :as model]
   [ui.view.opensearch-value.currency :as currency]))

(defn- registry-field-title
  [field-path]
  (let [attribute (if (sequential? field-path)
                    (last field-path)
                    field-path)
        field-schema (when attribute
                       (registry/get-field-schema @session/tenant-schema attribute))]
    (when attribute
      (loc/field-name attribute field-schema))))

(defn compile-card-field
  [field-path]
  (let [attribute (if (sequential? field-path)
                    (last field-path)
                    field-path)
        field-schema (when attribute
                       (registry/get-field-schema @session/tenant-schema attribute))
        alias (os-utils/field-path->alias field-path)
        currency-field-path (currency/currency-field-path {:field-path   field-path
                                                           :field-schema field-schema})]
    {:field-path field-path
     :field-title (registry-field-title field-path)
     :data-type (:field/data-type field-schema)
     :number-multiplier (:field/number-multiplier field-schema)
     :alias      alias
     ;; easier for rendering
     :flat-path  ["flat" alias]
     :label-path ["labels" (str alias "_en")]
     ;; where the card has the currency of a currency value, if it asked for it
     :currency-flat-path (when currency-field-path
                           ["flat" (os-utils/field-path->alias currency-field-path)])}))

(defn compile-card-config
  [card-config]
  (let [compile-vec (fn [xs] (mapv compile-card-field (or xs [])))]
    {:header-fields (compile-vec (:header-fields card-config))
     :body-fields   (compile-vec (:body-fields card-config))
     :footer-fields (compile-vec (:footer-fields card-config))
     :show-labels?  (if (contains? card-config :show-labels?)
                      (boolean (:show-labels? card-config))
                      true)
     :highlight     (when-let [hp (:highlight card-config)]
                      (compile-card-field hp))}))

(defn card-config->field-paths
  [card-config]
  (->> (concat
        (:header-fields card-config)
        (:body-fields card-config)
        (:footer-fields card-config)
        (:meta-fields card-config)
        (:currency-fields card-config)
        (when-let [hp (:highlight card-config)] [hp]))
       (remove nil?)
       distinct
       vec))

(defn init-runtime
  "Pure constructor for runtime state. No side effects."
  [config]
  (let [card-config (:card-config config)
        compiled-card (compile-card-config card-config)
        fields (card-config->field-paths card-config)]
    {:config          config
     :compiled-config {:card-config compiled-card
                       :fields      fields}
     :rev             0

     ;; Column metadata is driven by facets for the group-by field.
     :columns         {:status   :idle
                       :order    []
                       :by-value {}}

     :row-groups      {:status   :idle
                       :order    []
                       :by-value {}}

     ;; Which swimlanes and columns are in or near view. Only these cells load their cards.
     ;; NOTE: viewport state is runtime only (not persisted).
     :viewport        {:visible-rows    #{}
                       :rows            {}
                       :requested-cells #{}
                       :reloading-values #{}}

     ;; Cards are loaded per column value.
     ;; {:cards {<value> [card ...]}
     ;;  :paging {<value> {:start-index 0 :count 50 :next-cursor 50 :status :idle}}}
     ;; Each column value maps to a map with keys :items, :status, :next-cursor, :total, :has-more?, :error, :rev
     :cards-by-column {}

     :dnd             {:status :idle}
     :search          {}
     :errors          {}}))

(defn- assoc-error
  [db kanban-id ks err]
  (assoc-in db (into (model/errors-path kanban-id) (if (sequential? ks) ks [ks]))
            (or err true)))

(defn- clear-error
  [db kanban-id ks]
  (potpuri/dissoc-in db (into (model/errors-path kanban-id) (if (sequential? ks) ks [ks]))))

(def ^:private dropped ::dropped)

(defn- transit-safe
  "Best-effort sanitizer to guarantee transit-writable request payloads."
  [x]
  (cond
    (or (nil? x)
        (string? x)
        (number? x)
        (keyword? x)
        (boolean? x))
    x

    (vector? x)
    (->> x
         (map transit-safe)
         (remove #{dropped})
         vec)

    (set? x)
    (->> x
         (map transit-safe)
         (remove #{dropped})
         vec)

    (sequential? x)
    (->> x
         (map transit-safe)
         (remove #{dropped})
         vec)

    (map? x)
    (->> x
         (keep (fn [[k v]]
                 (let [k* (transit-safe k)
                       v* (transit-safe v)]
                   (when (and (not= k* dropped)
                              (not= v* dropped))
                     [k* v*]))))
         (into {}))

    :else
    dropped))

(rf/reg-event-fx
 ::init
 (fn [{:keys [db]} [_ {:keys [kanban-id config] :as _props}]]
   {:db (assoc-in db (model/kanban-path kanban-id) (init-runtime (or config {})))

     ;; Init loads facet columns immediately.
     ;; If you want strictly "load only when visible", you can remove this and trigger later.
    :fx [[:dispatch [::load-columns {:kanban-id kanban-id}]]
         [:dispatch [::load-row-groups {:kanban-id kanban-id}]]]}))

(rf/reg-event-fx
 ::destroy
 (fn [{:keys [db]} [_ {:keys [kanban-id]}]]
   {:db (potpuri/dissoc-in db (model/kanban-path kanban-id))}))

;; Kanban columns are loaded via the dedicated kanban endpoint (:search/kanban-columns)
;; rather than the facets endpoint. Use :search/facets only for the filter UI.

(rf/reg-event-fx
 ::load-columns
 (fn [{:keys [db]} [_ {:keys [kanban-id keep-viewport?]}]]
   (let [config (get-in db (model/config-path kanban-id))
         {:keys [entity-type group-by-field hide-empty-columns? pipeline-id opensearch-queries kanban-column-sort-mode]} config
         opensearch-queries (transit-safe opensearch-queries)
         limit (or (get-in config [:columns :max-columns]) 200)
         order (kanban-sort/normalize-column-sort-mode (or kanban-column-sort-mode
                                                           (get-in config [:columns :order])))
         body {:entity-type    entity-type
               :group-by-field group-by-field
               :hide-empty-columns? (boolean hide-empty-columns?)
               :pipeline-id    pipeline-id
               :opensearch-queries opensearch-queries
               :limit          limit
               :order          order}]
     (if (and entity-type group-by-field)
       {:db         (-> db
                        (assoc-in (into (model/columns-path kanban-id) [:status]) :loading)
                        (clear-error kanban-id :load-columns))
        ::ajax/send {:operation :search/kanban-columns
                     :body      body
                     :on-ok     [::load-columns-ok {:kanban-id      kanban-id
                                                    :keep-viewport? keep-viewport?}]
                     :on-error  [::load-columns-failed {:kanban-id kanban-id}]}}
       {:db (-> db
                (assoc-in (into (model/columns-path kanban-id) [:status]) :error)
                (assoc-error kanban-id :load-columns {:message "Missing kanban config: :entity-type and/or :group-by-field is nil"}))}))))

(defn load-columns-ok-fx
  "Store the columns. The first load of a board with no rows shows the first five
  columns. A load that keeps the viewport shows the columns that the user sees now,
  and loads their cards again. A board with rows has a strip of columns for each
  row. Each strip reports its columns when it is shown, and the load of the rows
  loads the cards again."
  [{db :db} [_ {:keys [kanban-id keep-viewport?]} resp]]
  (let [columns (or (get-in resp [:body :columns])
                    (get-in resp [:body :buckets])
                    [])
        ordered-columns (->> columns
                             (mapv (fn [{:keys [value]}] value)))
        initial-visible (set (take 5 ordered-columns))
        by-val (->> columns
                    (map (fn [{:keys [value label count] :as b}]
                           [value {:value value
                                   :label label
                                   :count count
                                   :raw   b}]))
                    (into {}))
        row-group-by-field (get-in db (into (model/config-path kanban-id) [:row-group-by-field]))]
    {:db (-> db
             (assoc-in (model/columns-path kanban-id) {:status   :loaded
                                                       :order    ordered-columns
                                                       :by-value by-val})
             (clear-error kanban-id :load-columns))
     :fx (cond
           (and (not keep-viewport?)
                (nil? row-group-by-field))
           [[:dispatch [::set-visible-columns {:kanban-id       kanban-id
                                               :row-group-value nil
                                               :visible-values  initial-visible
                                               :buffer-values   initial-visible}]]]

           (and keep-viewport?
                (nil? row-group-by-field))
           [[:dispatch [::reload-visible-columns {:kanban-id kanban-id}]]]

           :else
           [])}))

(rf/reg-event-fx
 ::load-columns-ok
 load-columns-ok-fx)

(defn refresh-fx
  "Load the columns and the rows again, after the data of the board changed in
  another part of the UI. Keep the columns that the user sees. A board that is
  not open stays as it is."
  [{:keys [db]} [_ {:keys [kanban-id]}]]
  (when (get-in db (model/config-path kanban-id))
    {:fx [[:dispatch [::load-columns {:kanban-id      kanban-id
                                      :keep-viewport? true}]]
          [:dispatch [::load-row-groups {:kanban-id kanban-id}]]]}))

(rf/reg-event-fx
 ::refresh
 refresh-fx)

(rf/reg-event-db
 ::load-columns-failed
 (fn [db [_ {:keys [kanban-id]} err]]
   (-> db
       (assoc-in (into (model/columns-path kanban-id) [:status]) :error)
       (assoc-error kanban-id :load-columns err))))

(rf/reg-event-fx
 ::load-row-groups
 (fn [{:keys [db]} [_ {:keys [kanban-id]}]]
   (let [config (get-in db (model/config-path kanban-id))
         {:keys [entity-type row-group-by-field row-group-sort-mode hide-empty-columns? pipeline-id opensearch-queries kanban-column-sort-mode]} config
         opensearch-queries (transit-safe opensearch-queries)
         limit (or (get-in config [:row-groups :max-groups]) 200)
         order (kanban-sort/normalize-row-sort-mode (or (get-in config [:row-groups :order])
                                                        row-group-sort-mode
                                                        kanban-column-sort-mode))
         body {:entity-type entity-type
               :group-by-field row-group-by-field
               :hide-empty-columns? (boolean hide-empty-columns?)
               :pipeline-id pipeline-id
               :opensearch-queries opensearch-queries
               :limit limit
               :order order}]
     (if (and entity-type row-group-by-field)
       {:db (-> db
                (assoc-in (into (model/row-groups-path kanban-id) [:status]) :loading)
                (clear-error kanban-id :load-row-groups))
        ::ajax/send {:operation :search/kanban-columns
                     :body body
                     :on-ok [::load-row-groups-ok {:kanban-id kanban-id}]
                     :on-error [::load-row-groups-failed {:kanban-id kanban-id}]}}
       {:db (assoc-in db (model/row-groups-path kanban-id) {:status :loaded
                                                            :order []
                                                            :by-value {}})}))))

(defn load-row-groups-ok-fx
  "Store the rows, and load again the cards that are in view. A field with many
  values, such as the jurisdiction, gives hundreds of rows. The cards of a row are
  loaded when the row is in view, and an empty row has no cards to load. The first
  load finds no cell in view yet, and each strip loads its cells when it reports its
  columns."
  [{:keys [db]} [_ {:keys [kanban-id]} resp]]
  (let [rows (or (get-in resp [:body :columns])
                 (get-in resp [:body :buckets])
                 [])
        ordered-rows (mapv (fn [{:keys [value]}]
                             value)
                           rows)
        by-val (->> rows
                    (map (fn [{:keys [value label count] :as row}]
                           [value {:value value
                                   :label label
                                   :count count
                                   :raw row}]))
                    (into {}))]
    {:db (-> db
             (assoc-in (model/row-groups-path kanban-id) {:status :loaded
                                                          :order ordered-rows
                                                          :by-value by-val})
             (clear-error kanban-id :load-row-groups))
     :fx [[:dispatch [::reload-visible-columns {:kanban-id kanban-id}]]]}))

(rf/reg-event-fx
 ::load-row-groups-ok
 load-row-groups-ok-fx)

(rf/reg-event-db
 ::load-row-groups-failed
 (fn [db [_ {:keys [kanban-id]} err]]
   (-> db
       (assoc-in (into (model/row-groups-path kanban-id) [:status]) :error)
       (assoc-error kanban-id :load-row-groups err))))

(defn- row-grouped?
  [db kanban-id]
  (some? (get-in db (into (model/config-path kanban-id) [:row-group-by-field]))))

(defn- rows-in-view
  "The rows whose cards may load now. A board with no rows has one row, `nil`. A board
  with rows loads the rows that are in or near view, and not the empty rows. An empty
  row is shown as a header."
  [db kanban-id]
  (if-not (row-grouped? db kanban-id)
    [nil]
    (let [row-order (get-in db (into (model/row-groups-path kanban-id) [:order]))
          row-metas (get-in db (into (model/row-groups-path kanban-id) [:by-value]))
          visible-rows (get-in db (into (model/viewport-path kanban-id) [:visible-rows]) #{})]
      (->> row-order
           (filter (fn [row-value]
                     (contains? visible-rows row-value)))
           (remove (fn [row-value]
                     (model/row-group-empty? (get row-metas row-value))))
           vec))))

(defn- row-column-values
  "The columns of one row strip, from `columns-key`: `:active-values` or `:visible-values`."
  [db kanban-id row-value columns-key]
  (get-in db (into (model/viewport-path kanban-id) [:rows row-value columns-key]) #{}))

(defn- cells-in-view
  "The `[row column]` cells that are in view now: the rows that are in view, each with
  the columns that its strip reports. `columns-key` is `:active-values`, the columns
  that the strip has made active, or `:visible-values`, the columns that the user sees."
  [db kanban-id columns-key]
  (vec (for [row-value (rows-in-view db kanban-id)
             column-value (row-column-values db kanban-id row-value columns-key)]
         [row-value column-value])))

(defn- cells-to-request
  "The cells that are in view and that nobody has asked for yet."
  [db kanban-id]
  (let [requested (get-in db (into (model/viewport-path kanban-id) [:requested-cells]) #{})]
    (->> (cells-in-view db kanban-id :active-values)
         (remove (fn [cell]
                   (contains? requested cell))))))

(defn- request-cells
  "Mark `cells` as asked for, and load their first page of cards."
  [db kanban-id cells]
  {:db (update-in db (into (model/viewport-path kanban-id) [:requested-cells]) (fnil into #{}) cells)
   :fx (mapv (fn [[row-value column-value]]
               [:dispatch [::load-cards {:kanban-id       kanban-id
                                         :row-group-value row-value
                                         :column-value    column-value
                                         :start-index     0
                                         :count           50}]])
             cells)})

(defn- card-state-key
  [row-group-value column-value]
  (if (some? row-group-value)
    [row-group-value column-value]
    column-value))

;; -------------------------
;; Viewport -> cells in view
;; -------------------------

(defn set-visible-columns-fx
  "Remember the columns that the strip of one row reports, and load the cards of the
  cells that are in view and were not asked for yet.

  Only the cells in view load: a row that is in view, and a column that its strip
  shows. A column that comes into view in one row does not load for the other rows.
  The set of active columns of a strip only grows, so a report that is a little
  narrower than the one before it does nothing. A board with hundreds of rows sent
  more than 100000 card requests in a few seconds when each report replaced a
  set that all the rows shared (DEV-8218)."
  [{:keys [db]} [_ {:keys [kanban-id row-group-value visible-values buffer-values]}]]
  (let [path (into (model/viewport-path kanban-id) [:rows row-group-value])
        old-active (get-in db (conj path :active-values) #{})
        reported (set (or buffer-values visible-values #{}))
        db-after (assoc-in db path {:visible-values (set (or visible-values #{}))
                                    :buffer-values  (set (or buffer-values #{}))
                                    :active-values  (cset/union old-active reported)})]
    (request-cells db-after kanban-id (cells-to-request db-after kanban-id))))

(rf/reg-event-fx
 ::set-visible-columns
 set-visible-columns-fx)

(defn set-visible-rows-fx
  "Remember the rows that are in or near view, and load the cards of the cells that
  are in view and were not asked for yet. Each row is a strip of columns. A row that
  is not in view has no cells to load."
  [{:keys [db]} [_ {:keys [kanban-id visible-values buffer-values]}]]
  (let [db-after (assoc-in db
                           (into (model/viewport-path kanban-id) [:visible-rows])
                           (set (or buffer-values visible-values #{})))]
    (request-cells db-after kanban-id (cells-to-request db-after kanban-id))))

(rf/reg-event-fx
 ::set-visible-rows
 set-visible-rows-fx)

(defn reload-visible-columns-fx
  "Load the cards again, for the cells that are in view: the rows that are in view,
  each with the columns that its strip shows."
  [{:keys [db]} [_ {:keys [kanban-id]}]]
  (let [cells (cells-in-view db kanban-id :visible-values)
        {db-after :db fx :fx} (request-cells db kanban-id cells)]
    {:db (assoc-in db-after
                   (into (model/viewport-path kanban-id) [:reloading-values])
                   (set (map second cells)))
     :fx fx}))

(rf/reg-event-fx
 ::reload-visible-columns
 reload-visible-columns-fx)

(defn- ensure-column-state
  [db kanban-id card-key]
  (let [path (into (model/cards-by-column-path kanban-id) [card-key])]
    (if (get-in db path)
      db
      (assoc-in db path {:status      :idle
                         :error       nil
                         :items       []
                         :next-cursor nil
                         :total       nil
                         :has-more?   true
                         :rev         0}))))

(defn- card-id
  [card]
  (or (get card "original.db/id")
      (get card :original.db/id)
      (:db/id card)
      (:id card)))

(defn- resolve-entity-attribute-by-name
  [entity-type field-name]
  (when (and entity-type (string? field-name) (seq field-name))
    (let [entity-schema (registry/get-schema @session/tenant-schema entity-type)
          resolved (->> (keys entity-schema)
                        (filter keyword?)
                        (some (fn [attribute]
                                (when (= (name attribute) field-name)
                                  attribute))))]
      (when-not resolved
        (logging/error "Failed to resolve non-namespaced group-by-field leaf to entity attribute"
                       {:entity-type entity-type
                        :field-name field-name}))
      resolved)))

(defn- leaf-field
  [field-path entity-type]
  (let [leaf (cond
               (keyword? field-path) field-path
               (sequential? field-path) (last field-path)
               :else nil)]
    (cond
      (nil? leaf)
      (if (and (nil? field-path)
               (nil? entity-type))
        nil
        (do (logging/error "Kanban group-by-field has invalid shape"
                           {:field-path field-path
                            :entity-type entity-type})
            nil))

      (not (keyword? leaf))
      (do (logging/error "Kanban group-by-field has invalid shape"
                         {:field-path field-path
                          :entity-type entity-type})
          nil)

      (namespace leaf)
      leaf

      :else
      (do (logging/warn "Kanban group-by-field leaf keyword is missing namespace"
                        {:field-path field-path
                         :leaf leaf
                         :entity-type entity-type})
          (or (resolve-entity-attribute-by-name entity-type (name leaf))
              (do (logging/error "Kanban group-by-field leaf keyword is missing namespace and cannot be resolved"
                                 {:field-path field-path
                                  :leaf leaf
                                  :entity-type entity-type})
                  nil))))))

(defn- static-option-column-value
  [opt]
  (when (map? opt)
    (let [raw-value (or (:keyword opt)
                        (:key opt)
                        (:value opt))]
      (cond
        (keyword? raw-value) (cu/kw->str raw-value)
        (or (string? raw-value)
            (number? raw-value)
            (boolean? raw-value)) raw-value
        :else (some-> raw-value str)))))

(defn- static-option-update-value
  [opt]
  (or (:keyword opt)
      (:key opt)
      (:value opt)))

(defn- matching-static-option
  [field-schema column-value]
  (some (fn [opt]
          (when (= (static-option-column-value opt) column-value)
            opt))
        (:options field-schema)))

(defn- persistable-column-value
  [field-schema column-value]
  (cond
    (nil? column-value)
    nil

    (= :data.type/keyword (:field/data-type field-schema))
    (if-let [opt (matching-static-option field-schema column-value)]
      (static-option-update-value opt)
      (if (string? column-value)
        (keyword column-value)
        column-value))

    :else
    column-value))

(defn- normalize-column-value
  [column-value]
  (if (nil? column-value)
    nil
    column-value))

(defn- remove-card-by-id
  [items card-id-value]
  (->> (or items [])
       (remove (fn [card]
                 (= (card-id card) card-id-value)))
       vec))

(defn- prepend-card-if-missing
  [items card]
  (let [items (vec (or items []))
        id-value (card-id card)]
    (if (some (fn [existing]
                (= (card-id existing) id-value))
              items)
      items
      (into [card] items))))

(defn- grouped-field-card-value-paths
  [group-by-field]
  (let [field-path (if (sequential? group-by-field)
                     (vec group-by-field)
                     [group-by-field])
        legacy-field-path (if (and (> (count field-path) 1)
                                   (keyword? (first field-path))
                                   (= "entity.type" (namespace (first field-path))))
                            (subvec field-path 1)
                            field-path)
        aliases (distinct [(os-utils/field-path->alias field-path)
                           (os-utils/field-path->alias legacy-field-path)])
        alias (first aliases)
        alias-key (keyword alias)
        label-alias (str alias "_en")
        label-alias-key (keyword label-alias)
        cleanup-flat-paths (mapcat (fn [alias]
                                     (let [alias-key (keyword alias)]
                                       [["flat" alias]
                                        ["flat" alias-key]
                                        [:flat alias]
                                        [:flat alias-key]]))
                                   aliases)
        cleanup-label-paths (mapcat (fn [alias]
                                      (let [label-alias (str alias "_en")
                                            label-alias-key (keyword label-alias)]
                                        [["labels" label-alias]
                                         ["labels" label-alias-key]
                                         [:labels label-alias]
                                         [:labels label-alias-key]]))
                                    aliases)
        cleanup-flat-keys (mapcat (fn [alias]
                                    [(str "flat." alias)
                                     (keyword (str "flat." alias))])
                                  aliases)
        cleanup-label-keys (mapcat (fn [alias]
                                     (let [label-alias (str alias "_en")]
                                       [(str "labels." label-alias)
                                        (keyword (str "labels." label-alias))]))
                                   aliases)]
    {:flat-paths [["flat" alias]
                  ["flat" alias-key]
                  [:flat alias]
                  [:flat alias-key]]
     :flat-keys [(str "flat." alias)
                 (keyword (str "flat." alias))]
     :label-paths [["labels" label-alias]
                   ["labels" label-alias-key]
                   [:labels label-alias]
                   [:labels label-alias-key]]
     :label-keys [(str "labels." label-alias)
                  (keyword (str "labels." label-alias))]
     :cleanup-flat-paths cleanup-flat-paths
     :cleanup-flat-keys cleanup-flat-keys
     :cleanup-label-paths cleanup-label-paths
     :cleanup-label-keys cleanup-label-keys}))

(defn- preferred-nested-path
  [m string-outer-key keyword-outer-key string-inner-key keyword-inner-key]
  (let [outer-key (cond
                    (contains? m string-outer-key) string-outer-key
                    (contains? m keyword-outer-key) keyword-outer-key
                    :else string-outer-key)
        nested (get m outer-key)
        inner-key (cond
                    (and (map? nested)
                         (contains? nested string-inner-key))
                    string-inner-key

                    (and (map? nested)
                         (contains? nested keyword-inner-key))
                    keyword-inner-key

                    :else
                    string-inner-key)]
    [outer-key inner-key]))

(defn- dissoc-paths
  [m paths]
  (reduce potpuri/dissoc-in m paths))

(defn- dissoc-keys
  [m ks]
  (reduce dissoc m ks))

(defn- assoc-keys
  [m ks value]
  (into m
        (map (fn [k]
               [k value]))
        ks))

(defn- assoc-grouped-field-card-value
  [card group-by-field column-value column-label]
  (let [{:keys [flat-keys label-keys
                cleanup-flat-paths cleanup-flat-keys cleanup-label-paths cleanup-label-keys]}
        (grouped-field-card-value-paths group-by-field)
        alias (os-utils/field-path->alias group-by-field)
        alias-key (keyword alias)
        label-alias (str alias "_en")
        label-alias-key (keyword label-alias)]
    (if (nil? column-value)
      (-> card
          (dissoc-paths (concat cleanup-flat-paths cleanup-label-paths))
          (dissoc-keys (concat cleanup-flat-keys cleanup-label-keys)))
      (let [column-label (or column-label (str column-value))
            flat-path (preferred-nested-path card "flat" :flat alias alias-key)
            label-path (preferred-nested-path card "labels" :labels label-alias label-alias-key)]
        (-> card
            (dissoc-paths (concat cleanup-flat-paths cleanup-label-paths))
            (dissoc-keys (concat cleanup-flat-keys cleanup-label-keys))
            (assoc-in flat-path column-value)
            (assoc-in label-path column-label)
            (assoc-keys flat-keys column-value)
            (assoc-keys label-keys column-label))))))

(defn- safe-dec
  [n]
  (max 0 (dec (long (or n 0)))))

(defn- safe-inc
  [n]
  (inc (long (or n 0))))

;; -------------------------
;; Cards (per-column loading)
;; -------------------------

(rf/reg-event-fx
 ::load-cards
 (fn [{:keys [db]} [_ {:keys [kanban-id row-group-value column-value start-index count]}]]
   (let [config (get-in db (model/config-path kanban-id))
         fields (get-in db (into (model/compiled-config-path kanban-id) [:fields]))
         column-meta (get-in db (into (model/columns-path kanban-id) [:by-value column-value :raw]))
         row-group-meta (get-in db (into (model/row-groups-path kanban-id) [:by-value row-group-value :raw]))
         {:keys [entity-type group-by-field row-group-by-field pipeline-id opensearch-queries sort]} config
         opensearch-queries (transit-safe opensearch-queries)
         count (or count 50)
         card-key (card-state-key row-group-value column-value)
         db (-> db
                (ensure-column-state kanban-id card-key)
                (assoc-in (into (model/cards-by-column-path kanban-id) [card-key :status]) :loading)
                (assoc-in (into (model/cards-by-column-path kanban-id) [card-key :error]) nil)
                (clear-error kanban-id [:load-cards card-key]))
         body {:entity-type    entity-type
               :pipeline-id    pipeline-id
               :group-by-field group-by-field
               :row-group-by-field row-group-by-field
               :row-group-column (when row-group-by-field
                                   (merge {:value row-group-value}
                                          (->> (select-keys (or row-group-meta {})
                                                            [:label :from :to])
                                               (remove (fn [[_k v]]
                                                         (nil? v)))
                                               (into {}))))
               :opensearch-queries opensearch-queries
               :sort           sort
               :column         (merge {:value  column-value
                                       :limit  count
                                       :cursor start-index}
                                      (->> (select-keys (or column-meta {})
                                                        [:label :from :to])
                                           (remove (fn [[_k v]]
                                                     (nil? v)))
                                           (into {})))
               :fields         fields}]
     (if (and entity-type group-by-field)
       {:db         db
        ::ajax/send {:operation :search/kanban-column
                     :body      body
                     :on-ok     [::load-cards-success {:kanban-id    kanban-id
                                                       :row-group-value row-group-value
                                                       :column-value column-value
                                                       :start-index  start-index}]
                     :on-error  [::load-cards-failure {:kanban-id    kanban-id
                                                       :row-group-value row-group-value
                                                       :column-value column-value}]}}
       {:db (-> db
                (ensure-column-state kanban-id card-key)
                (assoc-in (into (model/cards-by-column-path kanban-id) [card-key :status]) :error)
                (assoc-in (into (model/cards-by-column-path kanban-id) [card-key :error])
                          {:message "Missing kanban config: :entity-type and/or :group-by-field is nil"})
                (assoc-error kanban-id [:load-cards card-key]
                             {:message "Missing kanban config: :entity-type and/or :group-by-field is nil"}))}))))

(defn- hits->cards
  "Convert OpenSearch hits into UI card items.

  Preserve the document id so card click handlers can navigate to the entity."
  [hits]
  (->> (or hits [])
       (mapv (fn [hit]
               (let [source (or (get hit :_source)
                                (get hit "_source")
                                hit)
                     hit-id (or (get hit :_id)
                                (get hit "_id")
                                (get source "original.db/id")
                                (get source :original.db/id))
                     parsed-id (cond
                                 (number? hit-id) hit-id
                                 (string? hit-id) (let [n (js/parseInt hit-id 10)]
                                                    (when-not (js/isNaN n)
                                                      n))
                                 :else nil)]
                 (cond-> source
                   parsed-id
                   (assoc "original.db/id" parsed-id)))))))

(rf/reg-event-db
 ::load-cards-success
 (fn [db [_ {:keys [kanban-id row-group-value column-value start-index]} resp]]
    ;; The kanban-column endpoint returns:
    ;; {:operation :search/kanban-column
    ;;  :body {:column {:hits [...]
    ;;                 :next-cursor 50
    ;;                 :total 119
    ;;                 :requested {:limit 50 :cursor 0}
    ;;                 ...}
    ;;        ...}}
    ;;
    ;; Older/alternate shapes may return {:body {:cards [...] :next-cursor 50}}.
    ;; We support both shapes here.
   (let [column (get-in resp [:body :column])
         hits (or (get-in resp [:body :cards])
                  (get-in column [:hits])
                  (get-in resp [:body :hits])
                  [])
         cards (hits->cards hits)
         next-cursor (or (get-in resp [:body :next-cursor])
                         (get-in column [:next-cursor]))
         raw-has-more? (or (get-in resp [:body :has-more?])
                           (get-in column [:has-more?]))
          ;; Backwards compatible: if backend doesn't send :has-more?, derive it from next-cursor.
         has-more? (if (some? raw-has-more?)
                     (boolean raw-has-more?)
                     (boolean next-cursor))
         total (or (get-in column [:total])
                   (get-in resp [:body :total]))
         card-key (card-state-key row-group-value column-value)
         column-path (into (model/cards-by-column-path kanban-id) [card-key])
         items-path  (conj column-path :items)]
     (-> db
         (ensure-column-state kanban-id card-key)
         (update-in items-path
                    (fn [existing]
                      (if (zero? start-index)
                        (vec cards)
                        (into (vec (or existing [])) cards))))
         (assoc-in (conj column-path :next-cursor) next-cursor)
         (assoc-in (conj column-path :has-more?) has-more?)
         (assoc-in (conj column-path :total) total)
         (assoc-in (conj column-path :status) :loaded)
         (assoc-in (conj column-path :error) nil)
         (update-in (into (model/viewport-path kanban-id) [:reloading-values]) (fnil disj #{}) column-value)
         (clear-error kanban-id [:load-cards card-key])))))

(rf/reg-event-db
 ::load-cards-failure
 (fn [db [_ {:keys [kanban-id row-group-value column-value]} err]]
   (let [card-key (card-state-key row-group-value column-value)]
     (-> db
         (ensure-column-state kanban-id card-key)
         (assoc-in (into (model/cards-by-column-path kanban-id) [card-key :status]) :error)
         (assoc-in (into (model/cards-by-column-path kanban-id) [card-key :error]) err)
         (update-in (into (model/viewport-path kanban-id) [:reloading-values]) (fnil disj #{}) column-value)
         (assoc-error kanban-id [:load-cards card-key] err)))))

;; -------------------------
;; Drag/drop card move
;; -------------------------

(defn- apply-optimistic-move-db
  "The optimistic splice-and-recount shared by both move paths below: remove
   the card from its source column, prepend it to the target column, adjust
   totals/counts, and mark the kanban as :saving. Pulled out so the
   entry-criteria path (which must defer this until any criteria modal is
   submitted - see attempt-move-card-to-stage!) and the plain path (which
   applies it immediately) share identical bookkeeping."
  [db {:keys [kanban-id card source-column-value target-column-value]}]
  (let [id-value (card-id card)
        source-column-value (normalize-column-value source-column-value)
        target-column-value (normalize-column-value target-column-value)
        source-items-path (into (model/cards-by-column-path kanban-id) [source-column-value :items])
        target-items-path (into (model/cards-by-column-path kanban-id) [target-column-value :items])
        source-total-path (into (model/cards-by-column-path kanban-id) [source-column-value :total])
        target-total-path (into (model/cards-by-column-path kanban-id) [target-column-value :total])
        source-count-path (into (model/columns-path kanban-id) [:by-value source-column-value :count])
        target-count-path (into (model/columns-path kanban-id) [:by-value target-column-value :count])
        config (get-in db (model/config-path kanban-id))
        group-by-field (:group-by-field config)
        target-column-meta (get-in db (into (model/columns-path kanban-id) [:by-value target-column-value]))
        source-items (get-in db source-items-path [])
        target-items (get-in db target-items-path [])
        moved-card (or (some (fn [item]
                               (when (= (card-id item) id-value)
                                 item))
                             source-items)
                       card)
        optimistic-card (if group-by-field
                          (assoc-grouped-field-card-value moved-card
                                                          group-by-field
                                                          target-column-value
                                                          (:label target-column-meta))
                          moved-card)]
    (-> db
        (ensure-column-state kanban-id source-column-value)
        (ensure-column-state kanban-id target-column-value)
        (assoc-in source-items-path (remove-card-by-id source-items id-value))
        (assoc-in target-items-path (prepend-card-if-missing target-items optimistic-card))
        (update-in source-total-path safe-dec)
        (update-in target-total-path safe-inc)
        (update-in source-count-path safe-dec)
        (update-in target-count-path safe-inc)
        (assoc-in (into (model/dnd-path kanban-id) [:status]) :saving)
        (assoc-in (into (model/dnd-path kanban-id) [:error]) nil)
        (assoc-in (into (model/dnd-path kanban-id) [:last-move])
                  {:card-id id-value
                   :from source-column-value
                   :to target-column-value}))))

(defn- attempt-move-card-to-stage!
  "Pipeline-stage moves go through the same entry-criteria flow the old
   kanban and old grid use (frontend.view.entity/attempt-move-to-stage!) -
   identical trigger fn, identical modal, no changes needed there. Only
   `:db/id`/`:entity/type` are passed for `:opportunity` (never a full
   entity) - the OpenSearch-backed card is never hydrated into
   `@session/entities`, and criteria are resolved server-side against live
   Datomic state now, not the client cache. The splice is deferred into
   :before-save!, which that fn invokes once the backend has confirmed the
   move, so the card never jumps and then snaps back on a criteria-modal
   cancel. That also means it is not applied on the failure paths that end
   before any write - which is why ::move-card-failure gates its revert on
   [:dnd :last-move] rather than undoing unconditionally. Persistence goes through
   :entity/attempt-move-to-stage (not the plain field-update path other
   kanbans use), which also picks up move-to-stage's own side effects (e.g.
   deal volume-data tracking) for free."
  [{:keys [kanban-id card card-id entity-type source-column-value target-column-value]}]
  (f-entity/attempt-move-to-stage!
   {:opportunity {:db/id card-id :entity/type entity-type}
    :target-stage target-column-value
    :before-save! (fn []
                    (rf/dispatch [::apply-optimistic-move
                                  {:kanban-id kanban-id
                                   :card card
                                   :source-column-value source-column-value
                                   :target-column-value target-column-value}]))
    :on-success! (fn [_resp]
                   (rf/dispatch [::move-card-success {:kanban-id kanban-id}]))
    :on-error! (fn [err]
                 (rf/dispatch [::move-card-failure
                               {:kanban-id kanban-id
                                :card card
                                :card-id card-id
                                :source-column-value source-column-value
                                :target-column-value target-column-value
                                :error err}]))}))

(rf/reg-event-fx
 ::apply-optimistic-move
 (fn [{:keys [db]} [_ args]]
   {:db (apply-optimistic-move-db db args)}))

(rf/reg-event-fx
 ::move-card
 (fn [{:keys [db]} [_ {:keys [kanban-id card source-column-value target-column-value] :as args}]]
   (let [id-value (card-id card)
         source-column-value (normalize-column-value source-column-value)
         target-column-value (normalize-column-value target-column-value)]
     (if (or (nil? id-value)
             (= source-column-value target-column-value))
       {}
       (let [config (get-in db (model/config-path kanban-id))
             group-by-field (:group-by-field config)
             entity-type (:entity-type config)
             attribute (leaf-field group-by-field entity-type)]
         (if (pipeline/stage-attribute? attribute)
           (do
             (attempt-move-card-to-stage!
              {:kanban-id kanban-id
               :card card
               :card-id id-value
               :entity-type entity-type
               :source-column-value source-column-value
               :target-column-value target-column-value})
             {})
           (let [db* (apply-optimistic-move-db db args)]
             {:db db*
              :fx [[:dispatch [::persist-move
                               {:kanban-id           kanban-id
                                :card                card
                                :card-id             id-value
                                :source-column-value source-column-value
                                :target-column-value target-column-value}]]]})))))))

(rf/reg-event-fx
 ::persist-move
 (fn [{:keys [db]} [_ {:keys [kanban-id card-id target-column-value] :as args}]]
   (let [config (get-in db (model/config-path kanban-id))
         group-by-field (:group-by-field config)
         entity-type (:entity-type config)
         attribute (leaf-field group-by-field entity-type)
         field-schema (when attribute
                        (registry/get-field-schema @session/tenant-schema attribute))
         value (persistable-column-value field-schema (normalize-column-value target-column-value))]
     (if-not (and card-id attribute)
       {:db (-> db
                (assoc-in (into (model/dnd-path kanban-id) [:status]) :error)
                (assoc-in (into (model/dnd-path kanban-id) [:error])
                          {:message "Unable to persist kanban move"}))}
       (do
         (entity-actions/*->field-update-instant-indexing!
          card-id
          attribute
          value
          (fn [_resp]
            (rf/dispatch [::move-card-success {:kanban-id kanban-id}]))
          (fn [err]
            (rf/dispatch [::move-card-failure (assoc args :error err)])))
         {})))))

(rf/reg-event-db
 ::move-card-success
 (fn [db [_ {:keys [kanban-id]}]]
   (-> db
       ;; Consume the record ::move-card-failure gates its revert on, so a
       ;; later failure for a different card cannot match a stale entry and
       ;; undo a move that already succeeded.
       (assoc-in (into (model/dnd-path kanban-id) [:last-move]) nil)
       (assoc-in (into (model/dnd-path kanban-id) [:status]) :idle)
       (assoc-in (into (model/dnd-path kanban-id) [:error]) nil))))

(defn- error->message
  [error]
  (or (:message error)
      (when (string? error) error)
      (pr-str error)))

(rf/reg-event-fx
 ::move-card-failure
 (fn [{:keys [db]} [_ {:keys [kanban-id card source-column-value target-column-value error] :as payload}]]
   (let [id-value (or (get payload :card-id) (card-id card))
         source-column-value (normalize-column-value source-column-value)
         target-column-value (normalize-column-value target-column-value)
         source-items-path (into (model/cards-by-column-path kanban-id) [source-column-value :items])
         target-items-path (into (model/cards-by-column-path kanban-id) [target-column-value :items])
         source-total-path (into (model/cards-by-column-path kanban-id) [source-column-value :total])
         target-total-path (into (model/cards-by-column-path kanban-id) [target-column-value :total])
         source-count-path (into (model/columns-path kanban-id) [:by-value source-column-value :count])
         target-count-path (into (model/columns-path kanban-id) [:by-value target-column-value :count])
         source-items (get-in db source-items-path [])
         target-items (get-in db target-items-path [])
         moved-card (or card
                        (some (fn [item]
                                (when (= (card-id item) id-value)
                                  item))
                              target-items))
         ;; Only undo what was actually done. The item splices are idempotent,
         ;; but the total/count adjustments are plain arithmetic, so running
         ;; them without a matching apply-optimistic-move-db leaves the source
         ;; column reading one too high and the target one too low - and it
         ;; compounds per failure until the next refetch, with
         ;; hide-empty-columns? able to hide a column that still holds cards.
         ;;
         ;; Both matter because this handler has two callers with different
         ;; guarantees: ::persist-move always runs strictly after the splice,
         ;; while attempt-move-card-to-stage!'s :on-error! can fire with
         ;; nothing applied at all - the entry-criteria flow reports
         ;; :missing-target/:missing-criteria and a failed entity-scope
         ;; hydration, or an unexpected response, before any write is issued,
         ;; and frontend.view.entity/attempt-move-to-stage! only invokes
         ;; :before-save! once the backend has confirmed the move.
         applied? (= (get-in db (into (model/dnd-path kanban-id) [:last-move]))
                     {:card-id id-value
                      :from source-column-value
                      :to target-column-value})
         next-db (cond-> db
                   applied?
                   (-> (ensure-column-state kanban-id source-column-value)
                       (ensure-column-state kanban-id target-column-value)
                       (assoc-in target-items-path (remove-card-by-id target-items id-value))
                       (assoc-in source-items-path (prepend-card-if-missing source-items moved-card))
                       (update-in source-total-path safe-inc)
                       (update-in target-total-path safe-dec)
                       (update-in source-count-path safe-inc)
                       (update-in target-count-path safe-dec))

                   :always
                   (-> (assoc-in (into (model/dnd-path kanban-id) [:last-move]) nil)
                       (assoc-in (into (model/dnd-path kanban-id) [:status]) :error)
                       (assoc-in (into (model/dnd-path kanban-id) [:error]) error)))
         message (error->message error)]
     {:db next-db
      ::toast/error {:message message}})))
